import { ref } from 'vue';
import type { ContentRef } from '@codot/contracts';
import type { PublishItem, PublishTarget } from '../components/PublishDrawer.vue';

/**
 * "Create it, then publish it here": Publish New Content on a player or group can start a new composition,
 * playlist or campaign. The editor opens with this intent; its first save sends you back to where you were and
 * <PublishIntentHost> reopens the Publish drawer with the new item ticked (nothing is published until you confirm).
 */
export interface PublishIntent {
  target: PublishTarget;
  /** The page to come back to (route fullPath). */
  returnTo: string;
  /** Route name of the editor it opened (leaving both it and returnTo drops the intent). */
  editor: string;
  /** Set by the editor on its first save. */
  created: ContentRef | null;
}
export const publishIntent = ref<PublishIntent | null>(null);
/** Bumped after a publish made through the intent, so lists on the page underneath reload. */
export const intentPublishedVersion = ref(0);

export function startPublishIntent(target: PublishTarget, returnTo: string, editor: string): void {
  publishIntent.value = { target, returnTo, editor, created: null };
}
/** Is a new item being made for a player right now? (Its creator then returns instead of offering "Publish".) */
export const intentPending = (): boolean => !!publishIntent.value && !publishIntent.value.created;

/** "Publish now" from a toast anywhere (a finished upload, a new app…): <PublishIntentHost> opens the drawer. */
export const publishNow = ref<PublishItem[] | null>(null);
/** Uploads started from the Publish drawer: they're ticked there, so no "ready — publish?" toast. */
export const quietUploads = new Set<string>();
export function cancelPublishIntent(): void {
  publishIntent.value = null;
}
/** Editors call this after creating an item: the path to go back to, or null when there's no intent. */
export function completePublishIntent(created: ContentRef): string | null {
  const i = publishIntent.value;
  if (!i || i.created) return null;
  i.created = created;
  return i.returnTo;
}
